<template>
  <v-dialog
    :value="value"
    max-width="560"
    content-class="cmd-palette"
    transition="fade-transition"
    :scrollable="false"
    @input="$emit('input', $event)"
    @keydown.esc="close"
  >
    <div class="cmd" role="dialog" aria-modal="true" :aria-label="$t('nav_search_all')">
      <label class="cmd__field">
        <v-icon size="20">mdi-magnify</v-icon>
        <input
          ref="input"
          v-model="query"
          type="text"
          role="combobox"
          aria-expanded="true"
          aria-controls="cmd-list"
          :aria-activedescendant="results.length ? 'cmd-' + cursor : null"
          :aria-label="$t('nav_search_all')"
          :placeholder="$t('nav_search_placeholder')"
          autocomplete="off"
          @keydown.down.prevent="move(1)"
          @keydown.up.prevent="move(-1)"
          @keydown.enter.prevent="run(cursor)"
        />
      </label>
      <div id="cmd-list" ref="list" class="cmd__list" role="listbox">
        <div v-if="!results.length" class="cmd__empty">
          <b>{{ $t("nav_no_results", { q: query }) }}</b>
          <span>{{ $t("nav_no_results_hint") }}</span>
        </div>
        <template v-for="(row, i) in results">
          <div v-if="showHeading(i)" :key="'h-' + i" class="cmd__cap">{{ row.section }}</div>
          <button
            :id="'cmd-' + i"
            :key="'r-' + i"
            type="button"
            role="option"
            class="cmd__row"
            :class="{ 'is-on': i === cursor }"
            :aria-selected="String(i === cursor)"
            @click="run(i)"
            @mouseenter="cursor = i"
          >
            <span class="cmd__ri"><v-icon size="17" color="#c2410c">{{ row.icon }}</v-icon></span>
            <span class="cmd__rt">
              <b>{{ row.label }}</b>
              <span v-if="row.sub">{{ row.sub }}</span>
            </span>
            <kbd v-if="i === cursor" class="cmd__enter">Enter</kbd>
          </button>
        </template>
      </div>
      <div class="cmd__foot">
        <span><kbd>↑</kbd> <kbd>↓</kbd> {{ $t("nav_hint_move") }}</span>
        <span><kbd>Enter</kbd> {{ $t("nav_hint_open") }}</span>
        <span><kbd>Esc</kbd> {{ $t("nav_hint_close") }}</span>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import navMenu from "@/mixins/navMenu";
import { matchesText, normText } from "@/utils/searchText";
import { navState } from "@/utils/navPrefs";
import { navIcon } from "@/utils/menuGroups";
import { selectSite, applyLocale } from "@/utils/siteContext";

export default {
  name: "CommandPalette",
  mixins: [navMenu],
  props: {
    value: { type: Boolean, default: false },
    navMenu: { type: Array, default: () => [] },
    availableLanguages: { type: Array, default: () => [] },
    rail: { type: Boolean, default: false },
  },
  data: () => ({ query: "", cursor: 0, prevFocus: null }),
  computed: {
    user() {
      return this.$store.state.user.user;
    },
    pages() {
      const list = [];
      this.navMenu.forEach((item) => {
        if (item.child && item.child.length) {
          item.child.forEach((c) => list.push({ ...c, icon: c.icon || item.icon, _translate: true }));
        } else {
          list.push(item);
        }
      });
      return list.filter((item) => this.menuVisible(item));
    },
    sites() {
      const user = this.user;
      return user && user.type !== "admin_delivery" ? user.restaurants || [] : [];
    },
    results() {
      const q = this.query.trim();
      const out = [];
      const page = (item, section) => ({
        section,
        icon: navIcon(item),
        label: this.menuLabel(item),
        sub: this.$t("nav_pages"),
        run: () => this.go(item),
      });
      if (!q) {
        const byKey = {};
        this.pages.forEach((item) => (byKey[this.menuKey(item)] = item));
        const recent = navState.recent.map((k) => byKey[k]).filter(Boolean).slice(0, 3);
        recent.forEach((item) => out.push({ ...page(item, this.$t("nav_recent")), icon: "mdi-history" }));
        this.pages
          .filter((item) => !recent.includes(item))
          .slice(0, 6)
          .forEach((item) => out.push(page(item, this.$t("nav_pages"))));
      } else {
        this.pages
          .filter((item) => matchesText(this.menuLabel(item), q))
          .sort((a, b) => {
            const aStart = normText(this.menuLabel(a)).indexOf(normText(q)) === 0 ? 0 : 1;
            const bStart = normText(this.menuLabel(b)).indexOf(normText(q)) === 0 ? 0 : 1;
            return aStart - bStart;
          })
          .forEach((item) => out.push(page(item, this.$t("nav_pages"))));
        this.sites
          .filter((s) => matchesText(`${s.name} ${s.city || ""}`, q))
          .slice(0, 5)
          .forEach((site) =>
            out.push({
              section: this.$t("nav_sites"),
              icon: "mdi-storefront-outline",
              label: this.toLatin(site.name),
              sub: this.toLatin(site.city || site.address || ""),
              run: () => selectSite(this.$store, site),
            })
          );
      }
      this.actions
        .filter((a) => !q || matchesText(`${a.label} ${a.keys || ""}`, q))
        .forEach((a) => out.push({ section: this.$t("nav_actions"), sub: this.$t("nav_actions"), ...a }));
      return out;
    },
    actions() {
      const list = [];
      if (this.device.isDesktop) {
        list.push({
          label: this.rail ? this.$t("nav_expand") : this.$t("nav_collapse"),
          keys: "menu sine suzi prosiri",
          icon: "mdi-menu",
          run: () => this.$emit("toggle-rail"),
        });
      }
      this.availableLanguages
        .filter((l) => l.code !== this.$i18n.locale)
        .forEach((l) =>
          list.push({
            label: `${this.$t("nav_language")}: ${l.text}`,
            keys: "jezik pismo language",
            icon: "mdi-translate",
            run: () => applyLocale(this, l.code),
          })
        );
      list.push({ label: this.$t("logout"), keys: "odjava izlaz nalog logout", icon: "mdi-logout", run: () => this.$emit("logout") });
      return list;
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.prevFocus = document.activeElement;
        this.query = "";
        this.cursor = 0;
        this.$nextTick(() => setTimeout(() => this.$refs.input && this.$refs.input.focus(), 40));
      } else if (this.prevFocus && this.prevFocus.focus && document.contains(this.prevFocus)) {
        const el = this.prevFocus;
        this.$nextTick(() => el.focus());
      }
    },
    query() {
      this.cursor = 0;
    },
  },
  methods: {
    close() {
      this.$emit("input", false);
    },
    showHeading(i) {
      return i === 0 || this.results[i].section !== this.results[i - 1].section;
    },
    move(step) {
      const n = this.results.length;
      if (!n) return;
      this.cursor = (this.cursor + step + n) % n;
      this.$nextTick(() => {
        const el = document.getElementById("cmd-" + this.cursor);
        if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
      });
    },
    go(item) {
      if (!item.to) return;
      this.$router.push(item.to).catch(() => {});
    },
    run(index) {
      const row = this.results[index];
      if (!row) return;
      this.$emit("input", false);
      this.prevFocus = null; // fokus ostaje tamo gdje je radnja
      this.$nextTick(() => row.run());
    },
  },
};
</script>

<style lang="scss">
.cmd-palette.v-dialog {
  align-self: flex-start;
  margin-top: 9vh;
  border-radius: 18px;
  box-shadow: 0 30px 70px -10px rgba(40, 20, 5, 0.45) !important;
}
.cmd {
  background: #fff;
  &__field {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 12px;
    padding: 0 12px;
    height: 48px;
    border: 1px solid #e6d9cf;
    border-radius: 12px;
    color: #6b5a50;
    &:focus-within {
      border-color: #1d4ed8;
      box-shadow: 0 0 0 2px #dbe6ff;
    }
    input {
      flex: 1;
      min-width: 0;
      height: 100%;
      border: 0;
      outline: 0;
      background: transparent;
      font-size: 1rem;
      color: #1c1410;
    }
  }
  &__list {
    max-height: 340px;
    overflow-y: auto;
    padding-bottom: 6px;
  }
  &__cap {
    padding: 10px 16px 4px;
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #756559;
  }
  &__row {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    min-height: 46px;
    padding: 6px 16px;
    border: 0;
    background: transparent;
    text-align: left;
    cursor: pointer;
    &.is-on {
      background: #f7f1ed;
    }
  }
  &__ri {
    width: 32px;
    height: 32px;
    border-radius: 9px;
    background: #fff1ea;
    display: grid;
    place-items: center;
    flex: none;
  }
  &__rt {
    display: grid;
    min-width: 0;
    flex: 1;
    line-height: 1.25;
    b {
      font-weight: 600;
      font-size: 0.92rem;
      color: #1c1410;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    span {
      font-size: 0.74rem;
      color: #6b5a50;
    }
  }
  &__enter,
  &__foot kbd {
    font: 700 0.68rem "IBM Plex Sans", sans-serif;
    background: #fff !important;
    border: 1px solid #e6d9cf;
    border-radius: 5px;
    padding: 1px 5px;
    color: #6b5a50 !important;
    box-shadow: none !important;
  }
  &__empty {
    display: grid;
    gap: 6px;
    justify-items: center;
    text-align: center;
    padding: 22px 16px;
    font-size: 0.88rem;
    color: #6b5a50;
    b {
      color: #1c1410;
    }
  }
  &__foot {
    display: flex;
    gap: 14px;
    padding: 8px 16px;
    border-top: 1px solid #f0e2d9;
    background: #faf6f3;
    font-size: 0.72rem;
    color: #6b5a50;
  }
}
</style>
